// ============================================
// hooks/useAuth.js
// ============================================
import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserData } from '../models/userModel';

const isE2E = () => typeof window !== 'undefined' && !!window.Cypress;
const E2E_STORAGE_KEY = 'e2eLoggedIn';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    // In Cypress E2E, bypass Firebase and use a simple localStorage flag
    if (isE2E()) {
      const read = () => {
        try {
          const raw = window.localStorage.getItem(E2E_STORAGE_KEY);
          const parsed = raw ? JSON.parse(raw) : null;
          const authToken = window.localStorage.getItem('authToken');
          // If Cypress marks token as expired, force logout state
          const effectiveUser = authToken === 'expired-token' ? null : (parsed || null);
          setUser(effectiveUser);
          // emailVerified can be controlled explicitly for tests
          const ev = window.localStorage.getItem('e2eEmailVerified');
          const isVerified = ev != null ? ev !== 'false' : !!effectiveUser;
          setEmailVerified(isVerified);
          setUserData(null);
          setLoading(false);
        } catch (_) {
          setUser(null);
          setLoading(false);
        }
      };
      read();
      const onStorage = (e) => {
        if (e.key === E2E_STORAGE_KEY || e.key === 'e2eEmailVerified') read();
      };
      const onE2EChange = () => read();
      window.addEventListener('storage', onStorage);
      window.addEventListener('e2e-auth-change', onE2EChange);
      return () => {
        window.removeEventListener('storage', onStorage);
        window.removeEventListener('e2e-auth-change', onE2EChange);
      };
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setEmailVerified(firebaseUser.emailVerified);
        const data = await getUserData(firebaseUser.uid);
        setUserData(data);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Proactively refresh the user's auth state to detect email verification
  // after they click the link in their email, so the warning auto-dismisses.
  useEffect(() => {
    if (!user || emailVerified) return;

    let cancelled = false;
    const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

    const refresh = async () => {
      try {
        // Reload the current user to update properties like emailVerified
        await user.reload?.();
        if (!cancelled) {
          const verified = !!auth.currentUser?.emailVerified;
          if (verified !== emailVerified) {
            setEmailVerified(verified);
          }
        }
      } catch (_) {
        // noop – transient errors (offline, etc.) are okay
      }
    };

    // Initial immediate check
    refresh();

    // Poll while the tab is visible
    const intervalId = setInterval(() => {
      if (isBrowser && document.visibilityState !== 'visible') return;
      refresh();
    }, 5000);

    // Also refresh when window regains focus or tab becomes visible
    let focusHandler;
    let visibilityHandler;
    if (isBrowser) {
      focusHandler = () => refresh();
      visibilityHandler = () => {
        if (document.visibilityState === 'visible') refresh();
      };
      window.addEventListener('focus', focusHandler);
      document.addEventListener('visibilitychange', visibilityHandler);
    }

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (isBrowser) {
        window.removeEventListener('focus', focusHandler);
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
    };
  }, [user, emailVerified]);

  // Allow callers to force an immediate refresh
  const refreshAuthUser = async () => {
    if (isE2E()) {
      try {
        const raw = window.localStorage.getItem(E2E_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        setUser(parsed || null);
        setEmailVerified(true);
      } catch (_) {}
      return;
    }
    try {
      await auth.currentUser?.reload?.();
      setEmailVerified(!!auth.currentUser?.emailVerified);
      // Also sync the user reference in case Firebase updated it
      if (auth.currentUser && auth.currentUser !== user) {
        setUser(auth.currentUser);
      }
    } catch (_) {
      // ignore
    }
  };

  return { user, userData, loading, emailVerified, refreshAuthUser };
};